/**
 * Client-side Consent Recording Service
 * Writes consent events to the `user_consents` table (GDPR audit trail).
 * All methods are fire-and-forget: errors are logged but never thrown.
 */

import { supabase } from '@deskbay/shared/services/supabase/client';
import { logger } from '@deskbay/shared';
import { generateUUID } from '../utils/uuid';

/** Must match the DB CHECK constraint on user_consents.consent_type */
export type ConsentType =
    | 'cookie_essential'
    | 'cookie_analytics'
    | 'cookie_marketing'
    | 'privacy_policy'
    | 'terms'
    | 'geolocation'
    | 'push_notification';

interface RecordConsentParams {
    userId?: string;
    sessionId?: string;
    consentType: ConsentType;
    consentGiven: boolean;
    policyVersionId?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Records a single consent event in the database.
 * Fire-and-forget: never throws, always logs errors.
 */
export async function recordConsent(params: RecordConsentParams): Promise<void> {
    try {
        const { error } = await supabase
            .from('user_consents')
            .insert({
                user_id: params.userId ?? null,
                session_id: params.sessionId ?? null,
                consent_type: params.consentType,
                consent_given: params.consentGiven,
                policy_version_id: params.policyVersionId ?? null,
                ip_address: null,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                metadata: params.metadata ?? {},
            });

        if (error) {
            logger.security.warn('Failed to record consent', 'consentService', {
                consentType: params.consentType,
                errorMessage: error.message,
                errorCode: error.code,
            });
        }
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        logger.security.warn('Consent recording exception', 'consentService', {
            consentType: params.consentType,
            message,
        });
    }
}

/**
 * Records multiple consent events in parallel.
 * Useful for cookie banner (3 types at once) or onboarding (terms + privacy).
 * If a 'terms' consent is found with a userId, also stamps the profile.
 */
export async function recordMultipleConsents(
    consents: RecordConsentParams[]
): Promise<void> {
    await Promise.allSettled(consents.map(recordConsent));

    const termsConsent = consents.find(c => c.consentType === 'terms' && c.consentGiven);
    const userId = termsConsent?.userId;
    if (userId) {
        stampTermsAcceptance(userId, (termsConsent?.metadata?.acceptance_method as string) ?? 'clickwrap');
    }
}

/**
 * Stamps terms_accepted_at and acceptance_method on the user's profile.
 * Fire-and-forget: never throws.
 */
export async function stampTermsAcceptance(userId: string, method: string = 'clickwrap'): Promise<void> {
    try {
        const { error } = await supabase
            .from('profiles')
            .update({ terms_accepted_at: new Date().toISOString(), acceptance_method: method })
            .eq('id', userId);
        if (error) {
            logger.security.warn('Failed to stamp terms acceptance on profile', 'consentService', {
                userId, errorMessage: error.message,
            });
        }
    } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        logger.security.warn('stampTermsAcceptance exception', 'consentService', { userId, message });
    }
}

/** Storage key for anonymous session ID */
const SESSION_ID_KEY = 'deskbay_consent_session_id';

/**
 * Gets or creates a persistent session ID for anonymous users.
 * Stored in localStorage so it persists across page refreshes.
 */
export function getOrCreateSessionId(): string {
    try {
        const existing = localStorage.getItem(SESSION_ID_KEY);
        if (existing) return existing;
        const newId = generateUUID();
        localStorage.setItem(SESSION_ID_KEY, newId);
        return newId;
    } catch {
        return generateUUID();
    }
}
